import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faHome, faMapLocationDot, faChartLine, faHandshake, faComments, faList, faMap, faMicrophone, faStop } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
// KOPS-343: January 2, 2026 - Enhanced chatbot to be more consultative
// Original conversational service (kept for reference/rollback):
// import ChatRealEstateConversationalService from '../../services/chat-realestate-conversational.service';
// New consultative service that asks about lifestyle/motivation before searching:
import ChatRealEstateConsultativeService from '../../services/chat-realestate-consultative.service';
import InlinePropertyCards from '../../components/chat/InlinePropertyCards';
import PropertyResultsPanel from '../../components/chat/PropertyResultsPanel';
import MobileChatLayout from '../../components/chat/MobileChatLayout';
// import SearchProgressPanel from '../../components/chat/SearchProgressPanel'; // KOPS-361: Replaced with RightPanelWithLeadCapture
import SaveSearchModal from '../../components/chat/SaveSearchModal';
import PropertyDetailsModal from '../customer-portal/PropertyDetailsModal';
import buyerActivityService from '../../services/buyer-activity.service';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import './styles/chat-realestate.css';
import './styles/chat-realestate-mobile.css';
import RightPanelWithLeadCapture from '../../components/chat/RightPanelWithLeadCapture';
import ContactAgentPanel from '../../components/chat/ContactAgentPanel';
import leadCaptureTracking from '../../services/lead-capture-tracking.service';
import portalConfig from '../../services/portal-config.service';
import PortalInfoPanel from '../../components/chat/PortalInfoPanel';
import AppointmentModal from '../../components/portal/AppointmentModal';

// Buyer activity tracker — logs search intent to agent's activity feed (privacy-safe)
const trackBuyerActivity = async (eventType: string, data: any, agentId?: string, sessionId?: string, leadId?: string) => {
  if (!agentId) return;
  try {
    await axiosInstance.post('/portal/buyer-activity/track', {
      eventType,
      data,
      agentId,
      sessionId: sessionId || localStorage.getItem('portalSessionId') || undefined,
      leadId: leadId || localStorage.getItem('portalLeadId') || undefined,
    });
  } catch { /* non-blocking — don't break buyer experience */ }
};

// Message types
interface Message {
  id: string;
  type: 'user' | 'assistant' | 'listings' | 'system' | 'properties';
  content: string;
  listings?: any[];
  properties?: any[];
  // KOPS-343: Added suggested actions for consultative flow
  suggestedActions?: Array<{
    label: string;
    action: string;
  }>;
  timestamp: Date;
}

// Agent profile component
interface AgentProfile {
  name: string;
  photo: string;
  brokerage: string;
  phone: string;
  email: string;
  license: string;
  userId?: string;
}

// Minimal header with agent info
const MinimalHeader: React.FC<{ agent?: AgentProfile, isAISearchPortal?: boolean, onContactAgent?: () => void }> = ({ agent, isAISearchPortal, onContactAgent }) => {
  return (
    <header className="minimal-header border-bottom" style={{ background: 'white', position: 'sticky', top: 0, zIndex: 1000 }}>
      <Container fluid className="py-2">
        <Row className="align-items-center">
          {isAISearchPortal && agent && agent.brokerage && (
            <Col xs="auto" className="d-flex align-items-center me-3">
              {(agent as any).logo_url ? (
                <img
                  src={(agent as any).logo_url}
                  alt={agent.brokerage}
                  style={{ height: '40px', maxWidth: '160px', objectFit: 'contain' }}
                />
              ) : (
                <div>
                  <div className="fw-semibold" style={{ fontSize: '1rem' }}>{agent.brokerage}</div>
                </div>
              )}
            </Col>
          )}
          <Col>
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-3">{isAISearchPortal ? 'AI Search Portal' : 'chat.realestate'}</h5>
              <Badge bg="primary" className="me-2">AI-Powered</Badge>
              <small className="text-muted">Find homes by talking, not filtering</small>
            </div>
          </Col>
          {isAISearchPortal && agent && (
            <Col xs="auto" className="d-flex align-items-center">
              <img
                src={agent.photo}
                alt={agent.name}
                className="rounded-circle me-2"
                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
              />
              <div className="me-3">
                <div className="fw-bold small">{agent.name}</div>
                {/* ARMLS IDX 23.3.8 — brokerage name must display at a font size
                    equal to or LARGER than the agent's name (July 2026 audit). */}
                {agent.brokerage && (
                  <div className="fw-semibold small">{agent.brokerage}</div>
                )}
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onContactAgent?.()}
              >
                Contact Agent
              </Button>
            </Col>
          )}
          {!isAISearchPortal && (
            <Col xs="auto" className="d-flex align-items-center">
              <div className="me-3">
                <Button variant="link" size="sm" className="text-muted">About</Button>
                <Button variant="link" size="sm" className="text-muted">How it works</Button>
                <Button variant="link" size="sm" className="text-muted">Sign in</Button>
              </div>
              <img
                src={require('../../assets/img/logos/KairosIQLogov2.jpg')}
                alt="KairosIQ AI Realty"
                style={{ height: '35px', objectFit: 'contain' }}
              />
            </Col>
          )}
        </Row>
      </Container>
    </header>
  );
};

// Suggested prompts component
const SuggestedPrompts: React.FC<{ onSelectPrompt: (prompt: string) => void }> = ({ onSelectPrompt }) => {
  const prompts = [
    {
      icon: faHome,
      title: "First-time buyer",
      prompt: "I'm a first-time buyer looking for a starter home in Phoenix with good schools"
    },
    {
      icon: faMapLocationDot,
      title: "Relocating for work",
      prompt: "I'm relocating to Phoenix for work and need a place near downtown with a short commute"
    },
    {
      icon: faChartLine,
      title: "Investment property",
      prompt: "I'm interested in investment properties in Phoenix with good rental potential"
    },
    {
      icon: faHandshake,
      title: "Downsizing",
      prompt: "We're empty nesters looking to downsize to a smaller home with less maintenance"
    }
  ];

  return (
    <div className="suggested-prompts py-5">
      <h4 className="text-center mb-4">How can I help you find your next home?</h4>
      <Row className="g-3">
        {prompts.map((prompt, idx) => (
          <Col md={6} lg={3} key={idx}>
            <Card 
              className="h-100 cursor-pointer hover-shadow"
              onClick={() => onSelectPrompt(prompt.prompt)}
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={prompt.icon} size="2x" className="text-primary mb-3" />
                <h6>{prompt.title}</h6>
                <small className="text-muted">{prompt.prompt}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Listing card component for inline results - moved inside main component

// Message component - moved inside main component to access navigate and chatService

// Main chat interface component
interface HomePageV3Props {
  onPropertiesFound?: (properties: any[], criteria: any) => void;
  showMapButton?: boolean;
  onViewMap?: () => void;
  initialQuery?: string;
}

const HomePageV3: React.FC<HomePageV3Props> = ({ 
  onPropertiesFound, 
  showMapButton = false, 
  onViewMap,
  initialQuery 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasEnoughContext, setHasEnoughContext] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<any>(null);
  const [showProperties, setShowProperties] = useState(false);
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [properties, setProperties] = useState<any[]>([]);
  const [panelViewMode, setPanelViewMode] = useState<'list' | 'map'>('list');
  const [searchCriteria, setSearchCriteria] = useState<any>({});
  const [searchProgress, setSearchProgress] = useState<{
    matchCount: number;
    searchQuality: 'initial' | 'too_broad' | 'refining' | 'optimal' | 'ready' | 'no_results';
  }>({
    matchCount: 0,
    searchQuality: 'initial'
  });
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [hasShownSaveSearchModal, setHasShownSaveSearchModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // KOPS-343: Using consultative service for better conversation flow
  // const chatService = useRef(new ChatRealEstateConversationalService());
  const chatService = useRef(new ChatRealEstateConsultativeService());
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [mobileTab, setMobileTab] = useState<'chat' | 'list' | 'map'>('chat');
  const [savedPropertyIds, setSavedPropertyIds] = useState<Set<string>>(new Set());
  const [leadId, setLeadId] = useState<string | null>(null);
  const [agentSearchHistory, setAgentSearchHistory] = useState<any[]>([]);
  
  // Appointment scheduling modal
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentProperties, setAppointmentProperties] = useState<any[]>([]);
  const [isAISearchPortal] = useState(window.location.hostname.includes('aisearchportal.com') || window.location.hostname.includes('kairosiq.ai') || window.location.hostname.includes('kairosiq.ai'));
  
  // Agent profile - dynamically loaded from buyer context
  const [agentProfile, setAgentProfile] = useState<AgentProfile>({
    name: "Your Agent",
    photo: "https://placehold.co/100x100/1e3a8a/ffffff?text=Agent",
    brokerage: "",
    phone: "",
    email: "",
    license: ""
  });

  // Load agent profile from public API (aisearchportal product)
  useEffect(() => {
    const loadAgentFromAPI = async () => {
      try {
        // Extract agentId from URL path (/:agentId/:leadId) or query param
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const urlParams = new URLSearchParams(window.location.search);
        // Agent ID can be in query param, first path segment, or stored from DirectAccess
        const agentId = urlParams.get('agentId')
          || (pathParts.length >= 1 ? pathParts[0] : null)
          || localStorage.getItem('portalAgentId');

        if (!agentId || agentId.length < 3) return; // Skip if no valid agent ID or slug

        const response = await axiosInstance.get(`/public/agent-profile/${agentId}`);
        if (response.data?.success && response.data?.agent) {
          const a = response.data.agent;
          setAgentProfile({
            name: a.name || 'Your Agent',
            photo: a.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(a.name || 'Agent')}&background=1e3a8a&color=fff`,
            brokerage: a.brokerage || '',
            phone: a.phone || '',
            email: a.email || '',
            license: a.license || '',
            logo_url: a.logo_url || null,
            userId: agentId
          } as any);
          console.log('👤 [HomePageV3] Loaded agent from public API:', a.name);

          // Track portal visit — agent knows buyer is actively browsing
          // sessionId omitted here; trackBuyerActivity falls back to localStorage('portalSessionId')
          trackBuyerActivity('session_start', {}, agentId, undefined, leadId);
        }
      } catch (e) {
        console.warn('⚠️ [HomePageV3] Could not load agent profile from API');
      }
    };

    if (window.location.hostname.includes('aisearchportal.com') || window.location.hostname.includes('kairosiq.ai') || window.location.hostname.includes('kairosiq.ai')) {
      loadAgentFromAPI();
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Restore leadId from URL path (/:agentId/:leadId), query param (?leadId=), or localStorage
  // If leadId is in the URL path, call the direct access endpoint to load full buyer context
  useEffect(() => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const urlParams = new URLSearchParams(window.location.search);
    const pathAgentId = pathParts.length >= 1 ? pathParts[0] : null;
    const pathLeadId = pathParts.length >= 2 ? pathParts[1] : null;
    const queryLeadId = urlParams.get('leadId');
    const resolvedLeadId = pathLeadId || queryLeadId || localStorage.getItem('chat_lead_id');

    if (resolvedLeadId) {
      setLeadId(resolvedLeadId);
      localStorage.setItem('chat_lead_id', resolvedLeadId);

      // Call direct access endpoint to load full buyer context (same as DirectAccess.tsx)
      const agentId = pathAgentId || urlParams.get('agentId') || localStorage.getItem('portalAgentId');
      (async () => {
        try {
          // First try the full direct access endpoint (loads agent info + buyer context + search history)
          const { data } = await axiosInstance.get(`/portal/direct/${agentId}/${resolvedLeadId}`);
          if (data.success && data.data) {
            const { buyerContext, sanitizedBuyerContext } = data.data;
            console.log('🔗 [HomePageV3] Direct access loaded buyer context:', buyerContext);

            // Store contexts (same as DirectAccess.tsx)
            localStorage.setItem('portalBuyerContext', JSON.stringify(buyerContext));
            if (sanitizedBuyerContext) localStorage.setItem('portalBuyerContextSanitized', JSON.stringify(sanitizedBuyerContext));
            if (buyerContext.agentInfo) localStorage.setItem('portalAgentInfo', JSON.stringify(buyerContext.agentInfo));
            localStorage.setItem('portalDirectAccess', 'true');
            localStorage.setItem('portalAgentId', agentId || '');
            localStorage.setItem('portalLeadId', resolvedLeadId);
            sessionStorage.removeItem('autoSearchTriggered');

            // Update agent profile from buyer context
            if (buyerContext.agentInfo) {
              const a = buyerContext.agentInfo;
              setAgentProfile(prev => ({
                name: a.name || prev.name,
                photo: a.photo || prev.photo,
                brokerage: a.brokerage || prev.brokerage,
                phone: a.phone || prev.phone,
                email: a.email || prev.email,
                userId: a.id || agentId || (prev as any).userId,
                logo_url: a.logo_url || (prev as any).logo_url,
                license: a.license || prev.license,
              } as any));
            }

            // Extract search criteria from buyer context and auto-search
            const searchHistory = buyerContext.searchHistory || [];
            const buyerPrefs = buyerContext.buyerProfile?.property_preferences || {};
            const locPrefs = buyerContext.buyerProfile?.location_preferences || {};
            const prefs = {
              ...(typeof buyerPrefs === 'string' ? JSON.parse(buyerPrefs) : buyerPrefs),
              ...(typeof locPrefs === 'string' ? JSON.parse(locPrefs) : locPrefs),
            };

            if (Object.keys(prefs).length > 0) {
              setSearchCriteria(prefs);
              console.log('🏠 [HomePageV3] Buyer criteria loaded:', prefs);
              const searchDesc = [
                prefs.city || prefs.cities?.[0],
                prefs.bedrooms ? `${prefs.bedrooms} bedroom` : '',
                prefs.bathrooms ? `${prefs.bathrooms} bath` : '',
                prefs.budget_max ? `under $${Number(prefs.budget_max).toLocaleString()}` : '',
                prefs.property_types?.[0],
              ].filter(Boolean).join(', ');
              if (searchDesc && messages.length === 0) {
                handleSendMessage(searchDesc);
              }
            } else if (searchHistory.length > 0) {
              // Use last search as auto-search
              const lastSearch = searchHistory[0];
              if (lastSearch.query && messages.length === 0) {
                handleSendMessage(lastSearch.query);
              }
            }
          }
        } catch (e) {
          console.log('📋 [HomePageV3] Direct access failed, trying buyer profile fallback');
          // Fallback to simple buyer profile fetch
          try {
            const { data } = await axiosInstance.get(`/public-buyer-profile/${resolvedLeadId}`);
            if (data.success && data.profile) {
              const prefs = data.profile.property_preferences || {};
              const criteria = typeof prefs === 'string' ? JSON.parse(prefs) : prefs;
              if (Object.keys(criteria).length > 0) {
                setSearchCriteria(criteria);
                const searchDesc = [criteria.city, criteria.bedrooms ? `${criteria.bedrooms} bedroom` : ''].filter(Boolean).join(', ');
                if (searchDesc && messages.length === 0) handleSendMessage(searchDesc);
              }
            }
          } catch { console.log('📋 [HomePageV3] No buyer profile — fresh session'); }
        }
      })();
    }
  }, []);

  // Trigger auto-search for AISearchPortal with buyer context
  useEffect(() => {
    const isAISearchPortal = window.location.hostname.includes('aisearchportal.com') || window.location.hostname.includes('kairosiq.ai') || window.location.hostname.includes('kairosiq.ai');
    
    const triggerSearch = async () => {
      const buyerContextStr = localStorage.getItem('portalBuyerContext');
      console.log('🔍 [HomePageV3] Checking auto-search conditions:', {
        isAISearchPortal,
        hasBuyerContext: !!buyerContextStr,
        messagesLength: messages.length,
        buyerContext: buyerContextStr ? JSON.parse(buyerContextStr) : null
      });
      
      // Check if we should trigger auto-search
      if (isAISearchPortal && buyerContextStr) {
        // Check if we haven't already triggered (using a flag in sessionStorage)
        const hasTriggered = sessionStorage.getItem('autoSearchTriggered');
        
        if (!hasTriggered && messages.length === 0) {
          try {
            console.log('🔍 [HomePageV3] Triggering auto-search for AISearchPortal');
            sessionStorage.setItem('autoSearchTriggered', 'true');
            
            // Get the buyer context to extract search history and agent info
            const buyerContext = JSON.parse(buyerContextStr);
            
            // Load agent profile (preserve logo_url from public API if already loaded)
            if (buyerContext.agentInfo) {
              const agent = buyerContext.agentInfo;
              setAgentProfile(prev => ({
                name: agent.name || prev.name || "Your Agent",
                photo: agent.photo || prev.photo || `https://placehold.co/100x100/1e3a8a/ffffff?text=${(agent.name || 'Agent').split(' ').map((n: string) => n[0]).join('')}`,
                brokerage: agent.brokerage || prev.brokerage || "",
                phone: agent.phone || prev.phone || "",
                email: agent.email || prev.email || "",
                userId: agent.userId || agent.id || (prev as any).userId || '',
                logo_url: (prev as any).logo_url || agent.logo_url || null,
                license: agent.license || prev.license || ""
              } as any));
              console.log('👤 [HomePageV3] Loaded agent profile:', agent.name);
            }
            
            if (buyerContext.searchHistory && buyerContext.searchHistory.length > 0) {
              console.log('🔍 [HomePageV3] Found agent search history:', buyerContext.searchHistory);
              setAgentSearchHistory(buyerContext.searchHistory);
            }
            
            // Reinitialize chat service to ensure it picks up the buyer context
            chatService.current = new ChatRealEstateConsultativeService();
          
          // Wait a bit for service initialization
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const response = await chatService.current.triggerInitialSearch();
          console.log('🔍 [HomePageV3] Auto-search response:', response);
          
          if (response && response.message) {
            // Add the auto-generated message
            const userMessage: Message = {
              id: Date.now().toString(),
              type: 'user',
              content: `Hi, I'm looking for properties based on the preferences my agent shared with you.`,
              timestamp: new Date()
            };
            
            const assistantMessage: Message = {
              id: (Date.now() + 1).toString(),
              type: 'assistant',
              content: response.message,
              timestamp: new Date(),
              properties: response.properties
            };
            
            setMessages([userMessage, assistantMessage]);
            
            if (response.properties && response.properties.length > 0) {
              setProperties(response.properties);
              setShowProperties(true); // Show properties panel
            }
          }
        } catch (error) {
          console.error('Failed to trigger auto-search:', error);
        }
        }
      }
    };
    
    // Load agent search history when clicking on an agent search
    const loadAgentSearch = async (searchId: string, searchQuery: string) => {
      try {
        console.log('📚 [HomePageV3] Loading agent search:', searchId, searchQuery);
        
        // Add a user message for the search
        const userMessage: Message = {
          id: Date.now().toString(),
          type: 'user',
          content: searchQuery,
          timestamp: new Date()
        };
        setMessages([userMessage]);
        setIsTyping(true);
        
        // Use the chat service to run the search
        const response = await chatService.current.sendMessage(searchQuery);
        
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: response.message,
          timestamp: new Date(),
          properties: response.properties
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        setIsTyping(false);
        
        if (response.properties && response.properties.length > 0) {
          setProperties(response.properties);
          setShowProperties(true);
        }
        
        // Update search criteria
        if (response.searchCriteria) {
          setSearchCriteria(response.searchCriteria);
        }
      } catch (error) {
        console.error('Failed to load agent search:', error);
        setIsTyping(false);
      }
    };
    
    // Expose loadAgentSearch to window for click handlers
    (window as any).loadAgentSearch = loadAgentSearch;

    // Try immediately on mount
    if (isAISearchPortal) {
      console.log('🏠 [HomePageV3] AISearchPortal detected, scheduling auto-search...');
      setTimeout(triggerSearch, 1500); // Give more time for localStorage to be set
    }
    
    // Also listen for the event from PortalRouter
    const handleBuyerContextLoaded = () => {
      console.log('🎯 [HomePageV3] Received portalBuyerContextLoaded event');
      setTimeout(triggerSearch, 100);
    };
    
    window.addEventListener('portalBuyerContextLoaded', handleBuyerContextLoaded);
    
    return () => {
      window.removeEventListener('portalBuyerContextLoaded', handleBuyerContextLoaded);
      // Clear the auto-search flag when unmounting
      if (isAISearchPortal) {
        sessionStorage.removeItem('autoSearchTriggered');
      }
    };
  }, []);

  // Initialize voice recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join(' ');
        if (inputRef.current) inputRef.current.value = transcript;
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
        if (inputValue.trim()) {
          handleSendMessage();
        }
      };

      recognitionInstance.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
    }
  }, []);

  // Initialize from URL on mount
  useEffect(() => {
    const query = searchParams.get('q');
    const criteria = searchParams.get('criteria');
    
    // Check for initial query from props (passed from homepage)
    if (initialQuery && !query) {
      if (inputRef.current) inputRef.current.value = initialQuery;
      setTimeout(() => {
        handleSendMessage(initialQuery);
      }, 100);
      return;
    }
    
    if (query) {
      // Restore search from URL
      if (inputRef.current) inputRef.current.value = query;
      setTimeout(() => {
        handleSendMessage(query);
      }, 100);
    }
    
    if (criteria) {
      try {
        const parsedCriteria = JSON.parse(criteria);
        setSearchCriteria(parsedCriteria);
      } catch (e) {
        console.error('Failed to parse criteria from URL:', e);
      }
    }
  }, [initialQuery]); // Add initialQuery to dependency array
  
  // Update URL when search criteria changes
  useEffect(() => {
    if (Object.keys(searchCriteria).length > 0) {
      const params = new URLSearchParams();
      
      // Build a human-readable query string
      const queryParts = [];
      if (searchCriteria.city) queryParts.push(searchCriteria.city);
      if (searchCriteria.beds) queryParts.push(`${searchCriteria.beds} bedroom`);
      if (searchCriteria.maxPrice) queryParts.push(`under ${searchCriteria.maxPrice}`);
      
      if (queryParts.length > 0) {
        params.set('q', queryParts.join(' '));
        params.set('criteria', JSON.stringify(searchCriteria));
        
        // Only update URL if it's actually different
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        const currentUrl = window.location.pathname + window.location.search;
        
        if (newUrl !== currentUrl) {
          // Update URL without causing navigation
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
  }, [searchCriteria]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const query = params.get('q');
      const criteria = params.get('criteria');
      
      if (query) {
        if (inputRef.current) inputRef.current.value = query;
        // Clear current messages and start fresh
        setMessages([]);
        setProperties([]);
        setTimeout(() => {
          handleSendMessage(query);
        }, 100);
      } else {
        // No query in URL, reset to initial state
        setMessages([]);
        setProperties([]);
        setSearchCriteria({});
        setShowProperties(false);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle view all properties event
  useEffect(() => {
    const handleViewAll = (event: CustomEvent) => {
      const { properties } = event.detail;
      setProperties(properties);
      setShowProperties(true);
      setPanelViewMode('list'); // Start with list view
    };

    window.addEventListener('viewAllProperties', handleViewAll as any);
    return () => {
      window.removeEventListener('viewAllProperties', handleViewAll as any);
    };
  }, []);

  // Handle saving/unsaving a property
  const handleToggleSaveProperty = async (property: any) => {
    try {
      const sessionId = chatService.current?.getSessionId() || `chat_${Date.now()}`;
      const isSaved = savedPropertyIds.has(property.id);
      
      if (isSaved) {
        // Unsave property
        const success = await buyerActivityService.unsaveProperty(property.mlsNumber || property.mls_number || property.id);
        if (success) {
          setSavedPropertyIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(property.id);
            return newSet;
          });
          
          // Show success message
          const messageEl = document.createElement('div');
          messageEl.className = 'save-notification';
          messageEl.textContent = '❌ Property removed from saved';
          document.body.appendChild(messageEl);
          setTimeout(() => messageEl.remove(), 3000);
        }
      } else {
        // Save property using buyer-activity service
        const success = await buyerActivityService.saveProperty({
          ...property,
          mlsNumber: property.mlsNumber || property.mls_number || property.id,
          address: `${property.address}, ${property.city}, ${property.state} ${property.zipCode}`,
          bedrooms: property.bedrooms || property.beds,
          bathrooms: property.bathrooms || property.baths,
          square_feet: property.square_feet || property.sqft || property.squareFeet
        });
        
        if (success) {
          setSavedPropertyIds(prev => new Set([...prev, property.id]));

          // Track property save — agent sees buyer interest
          trackBuyerActivity('property_save', {
            address: `${property.address}, ${property.city}`,
            price: property.price,
            mlsNumber: property.mlsNumber || property.mls_number,
            bedrooms: property.bedrooms || property.beds,
            bathrooms: property.bathrooms || property.baths,
          }, (agentProfile as any)?.userId, sessionId, leadId);

          // Also try to save via API for backend persistence
          try {
            const response = await axiosInstance.post('/public-buyer-profile/save-property', {
              sessionId,
              leadId,
              mlsNumber: property.mlsNumber || property.mls_number,
              propertyData: {
                address: property.address,
                city: property.city,
                state: property.state,
                zipCode: property.zipCode,
                price: property.price,
                bedrooms: property.bedrooms,
                bathrooms: property.bathrooms,
                squareFeet: property.squareFeet || property.square_feet,
                images: property.images
              }
            });
            
            if (response.data.success) {
              // If this created a new lead, save the lead ID
              if (response.data.leadId && !leadId) {
                setLeadId(response.data.leadId);
                
                // Update buyer context for activity tracking
                const buyerContext = {
                  leadId: response.data.leadId,
                  sessionId: sessionId,
                  agentInfo: agentProfile
                };
                localStorage.setItem('portalBuyerContext', JSON.stringify(buyerContext));
              }
            }
          } catch (apiError) {
            console.log('Backend save not available, using local storage fallback');
          }
          
          // Show success message
          const messageEl = document.createElement('div');
          messageEl.className = 'save-notification';
          messageEl.textContent = '❤️ Property saved!';
          document.body.appendChild(messageEl);
          setTimeout(() => messageEl.remove(), 3000);
        }
      }
    } catch (error) {
      console.error('Error saving property:', error);
      // Could show a toast notification here
    }
  };

  // Handle email capture from right panel
  const handleEmailCapture = async (email: string) => {
    try {
      const sessionId = chatService.current?.getSessionId() || `chat_${Date.now()}`;
      
      // IMPORTANT: The backend will automatically detect white-label domains
      // from the x-portal-domain header and assign leads to the brokerage owner
      const response = await axiosInstance.post('/public-buyer-profile/create-profile', {
        sessionId,
        email,
        leadSource: 'right_panel',
        propertyPreferences: searchCriteria || {},
        conversationContext: {
          messageCount: messages.length,
          hasSearched: properties.length > 0,
          propertiesSaved: savedPropertyIds.size
        }
      });
      
      if (response.data.leadId) {
        setLeadId(response.data.leadId);
        leadCaptureTracking.captureEmail(email);

        // Track email capture — agent gets notified of new lead
        trackBuyerActivity('email_captured', {
          email,
          propertiesSaved: savedPropertyIds.size,
          searchCount: messages.filter(m => m.type === 'user').length,
        }, (agentProfile as any)?.userId, sessionId, response.data.leadId);

        // Update buyer context for activity tracking
        const buyerContext = {
          leadId: response.data.leadId,
          sessionId: sessionId,
          email: email,
          agentInfo: agentProfile
        };
        localStorage.setItem('portalBuyerContext', JSON.stringify(buyerContext));
        
        // Track conversion event
        leadCaptureTracking.trackInteraction();
        
        console.log('✅ Email captured and lead created:', response.data.leadId);
      }
    } catch (error) {
      console.error('Failed to capture email:', error);
      throw error; // Let the component handle the error display
    }
  };

  // Handle voice input toggle
  const toggleVoiceInput = () => {
    if (!recognition) {
      alert('Voice recognition is not supported in your browser. Please use Chrome or Safari.');
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
      if (inputRef.current) inputRef.current.value = ''; // Clear input when starting voice
    }
  };

  // Handle sending a message
  const handleSendMessage = async (content?: string) => {
    const msg = content || inputRef.current?.value || '';
    if (!msg.trim()) return;

    // Navigate to search page with the query for chat.realestate domains
    // But only if we're not already on the search page
    if (window.location.hostname.includes('chat.realestate') && 
        !window.location.pathname.includes('/search')) {
      const context = chatService.current.getContext();
      const queryParams = new URLSearchParams();
      queryParams.set('q', msg);
      if (context.extractedInfo && Object.keys(context.extractedInfo).length > 0) {
        queryParams.set('criteria', JSON.stringify(context.extractedInfo));
      }
      navigate(`/search?${queryParams.toString()}`);
      return;
    }

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: msg,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    if (inputRef.current) inputRef.current.value = '';
    setIsTyping(true);

    try {
      // Send to AI service
      const response = await chatService.current.sendMessage(msg);
      
      // Update search progress if provided
      if (response.searchProgress) {
        setSearchProgress(response.searchProgress);
      }
      
      // KOPS-443: Handle AI-triggered search action
      if (response.searchAction && response.searchAction.trigger && response.searchAction.type === 'new_search') {
        console.log('🤖 [HomePageV3] AI triggered a new search:', response.searchAction);
        
        // If we got searchExecuted=true, it means backend already ran the search
        if (response.searchExecuted && response.properties && response.properties.length > 0) {
          console.log('✅ [HomePageV3] Search already executed by backend, using results');
          // Properties will be handled below in the normal flow
        } else {
          console.log('⚡ [HomePageV3] Need to execute search with criteria:', response.searchAction.criteria);
          // The backend should have already executed the search, so this is just for logging
        }
      }
      
      // KOPS-443: Handle appointment scheduling intent
      if (response.appointmentAction && response.appointmentAction.trigger) {
        console.log('📅 [HomePageV3] AI detected appointment intent:', response.appointmentAction);
        
        // Collect properties for appointment scheduling
        const propsForAppointment = [];
        
        // Type-safe access to appointment action properties
        const appointmentData = response.appointmentAction as any;
        
        // Check if user mentioned a specific property address
        if (appointmentData.extractedAddress) {
          console.log('🏡 Extracted address:', appointmentData.extractedAddress);
          
          // First check if there's a specific property match in the response
          if (appointmentData.targetProperty) {
            propsForAppointment.push(appointmentData.targetProperty);
          } else {
            // Search for the property by address in current properties
            const addressLower = appointmentData.extractedAddress.toLowerCase();
            const foundProperty = properties.find(p => 
              p.address && p.address.toLowerCase().includes(addressLower)
            );
            
            if (foundProperty) {
              propsForAppointment.push(foundProperty);
            } else {
              // Create a placeholder property with the address
              propsForAppointment.push({
                mlsNumber: 'pending',
                address: appointmentData.extractedAddress,
                price: 0,
                bedrooms: 0,
                bathrooms: 0
              });
            }
          }
        } else {
          // No specific address - use recent properties or properties from current search
          if (properties.length > 0) {
            // Use currently displayed properties
            propsForAppointment.push(...properties.slice(0, 5)); // Top 5
          } else if (response.properties && response.properties.length > 0) {
            // Use properties from response
            propsForAppointment.push(...response.properties.slice(0, 5));
          }
        }
        
        // If we have properties to schedule
        if (propsForAppointment.length > 0) {
          setAppointmentProperties(propsForAppointment.map(p => ({
            mlsId: p.mlsNumber || p.id || 'pending',
            address: p.address,
            price: p.price || 0,
            beds: p.bedrooms || p.beds || 0,
            baths: p.bathrooms || p.baths || 0
          })));
          setShowAppointmentModal(true);
        } else {
          // No properties but still show modal for general inquiry
          setAppointmentProperties([]);
          setShowAppointmentModal(true);
        }
      }
      
      // Add assistant response with suggested actions (KOPS-343 enhancement)
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.message,
        suggestedActions: response.suggestedActions, // New: suggested actions for guided conversation
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // If AI mentioned specific properties, show them after the message
      if (response.mentionedProperties && response.mentionedProperties.length > 0) {
        console.log('📍 [HOME] AI mentioned properties:', response.mentionedProperties);
        
        const formattedMentioned = response.mentionedProperties.map((prop: any) => ({
          id: prop.id,
          mlsNumber: prop.mlsNumber,
          address: prop.address,
          city: prop.city,
          state: prop.state,
          zipCode: prop.zipCode,
          price: prop.price,
          bedrooms: prop.beds || prop.bedrooms,
          bathrooms: prop.baths || prop.bathrooms,
          squareFeet: prop.sqft || prop.squareFeet,
          images: prop.images || [],
          latitude: prop.latitude,
          longitude: prop.longitude,
          features: prop.features || [],
          listingBrokerage: prop.listingBrokerage,
          listingAgent: prop.listingAgent,
          description: prop.description,
          // Add missing fields for AI analysis
          pool: prop.pool,
          yearBuilt: prop.yearBuilt,
          garage: prop.garage,
          hoaFee: prop.hoaFee,
          daysOnMarket: prop.daysOnMarket,
          propertyType: prop.propertyType,
          lotSize: prop.lotSize,
          fireplace: prop.fireplace,
          viewType: prop.viewType,
          // Map the correct field names
          beds: prop.beds || prop.bedrooms,
          baths: prop.baths || prop.bathrooms,
          sqft: prop.sqft || prop.squareFeet,
          // PropertyCard expects square_feet
          square_feet: prop.sqft || prop.squareFeet || prop.square_feet || 0,
          // Enhanced fields for better AI responses
          subdivision: prop.subdivision,
          schoolDistrict: prop.schoolDistrict,
          priceReduction: prop.priceReduction,
          listingDate: prop.listingDate,
          modifiedDate: prop.modifiedDate,
          virtualTour: prop.virtualTour,
          spa: prop.spa,
          conditionInsights: prop.conditionInsights,
          mlsArea: prop.mlsArea,
          status: prop.status,
          // Parse features array if it exists
          allFeatures: prop.features || []
        }));
        
        const mentionedPropertiesMessage: Message = {
          id: (Date.now() + 1.5).toString(),
          type: 'properties',
          content: '',
          properties: formattedMentioned,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, mentionedPropertiesMessage]);
      }
      
      // Update context status (KOPS-343: now requires comprehensive context)
      const context = chatService.current.getContext();
      const hasContext = !!(context.hasComprehensiveInfo || (context.extractedInfo?.city || context.extractedInfo?.zipCode));
      setHasEnoughContext(hasContext);
      
      // Update search criteria for the progress panel
      // Only update if the criteria has actually changed to prevent re-renders
      if (context.extractedInfo) {
        const newCriteria = context.extractedInfo;
        const hasChanged = JSON.stringify(newCriteria) !== JSON.stringify(searchCriteria);
        if (hasChanged) {
          setSearchCriteria(newCriteria);
        }
      }
      
      // KOPS-343: Only show properties after meaningful conversation
      if (response.hasEnoughContext === false) {
        console.log('📝 [CHAT] Not enough context yet, continuing conversation...');
      }
      
      // If properties were returned, show them inline and in panel
      if (response.properties && response.properties.length > 0) {
        console.log('📍 [HOME] Properties received:', response.properties);
        
        const formattedProperties = response.properties.map((prop: any) => ({
          id: prop.id,
          mlsNumber: prop.mlsNumber,
          address: prop.address,
          city: prop.city,
          state: prop.state,
          zipCode: prop.zipCode,
          price: prop.price,
          bedrooms: prop.beds || prop.bedrooms,
          bathrooms: prop.baths || prop.bathrooms,
          squareFeet: prop.sqft || prop.squareFeet,
          images: prop.images || [],
          latitude: prop.latitude,
          longitude: prop.longitude,
          features: prop.features || [],
          listingBrokerage: prop.listingBrokerage,
          listingAgent: prop.listingAgent,
          description: prop.description,
          // Add missing fields for AI analysis
          pool: prop.pool,
          yearBuilt: prop.yearBuilt,
          garage: prop.garage,
          hoaFee: prop.hoaFee,
          daysOnMarket: prop.daysOnMarket,
          propertyType: prop.propertyType,
          lotSize: prop.lotSize,
          fireplace: prop.fireplace,
          viewType: prop.viewType,
          // Map the correct field names
          beds: prop.beds || prop.bedrooms,
          baths: prop.baths || prop.bathrooms,
          sqft: prop.sqft || prop.squareFeet,
          // PropertyCard expects square_feet
          square_feet: prop.sqft || prop.squareFeet || prop.square_feet || 0,
          // Enhanced fields for better AI responses
          subdivision: prop.subdivision,
          schoolDistrict: prop.schoolDistrict,
          priceReduction: prop.priceReduction,
          listingDate: prop.listingDate,
          modifiedDate: prop.modifiedDate,
          virtualTour: prop.virtualTour,
          spa: prop.spa,
          conditionInsights: prop.conditionInsights,
          mlsArea: prop.mlsArea,
          status: prop.status,
          // Parse features array if it exists
          allFeatures: prop.features || []
        }));
        
        // Check if this is a new search or just returning existing properties
        const isNewSearch = properties.length === 0 || formattedProperties.length !== properties.length;
        
        // Update state for right panel
        setProperties(formattedProperties);
        const chatContext = chatService.current.getContext();
        // Only update search criteria if it has changed
        const newCriteria = chatContext.extractedInfo || {};
        const hasChanged = JSON.stringify(newCriteria) !== JSON.stringify(searchCriteria);
        if (hasChanged) {
          setSearchCriteria(newCriteria);
        }

        // Track buyer search activity — agent sees what buyer is looking for
        if (formattedProperties.length > 0 && isNewSearch) {
          trackBuyerActivity('search', {
            query: messages[messages.length - 1]?.content || '',
            resultCount: formattedProperties.length,
            criteria: newCriteria,
          }, (agentProfile as any)?.userId, undefined, leadId);
        }
        
        // KOPS-361: Prompt for email/alerts before showing properties
        // Only show modal if we have properties, user hasn't provided email yet, and haven't shown modal before
        // KOPS-445: Skip save search modal for AI Search Portal users - they're already registered
        const skipSaveSearchModal = window.location.hostname.includes('aisearchportal.com') || window.location.hostname.includes('kairosiq.ai');
        
        if (formattedProperties.length > 0 && !leadId && !hasShownSaveSearchModal && !skipSaveSearchModal) {
          setShowSaveSearchModal(true);
          setHasShownSaveSearchModal(true); // Mark that we've shown the modal
          // Properties will be shown after modal is handled
        } else {
          // User already has account, no properties found, or modal already shown
          console.log('🔥 [HomePageV3] Setting showProperties to true because:', {
            hasProperties: formattedProperties.length > 0,
            hasLeadId: !!leadId,
            modalShown: hasShownSaveSearchModal
          });
          setShowProperties(true);
        }
        
        // Call parent callback if provided
        if (onPropertiesFound) {
          onPropertiesFound(formattedProperties, chatContext.extractedInfo || {});
        }
        
        // Only add properties message for new searches, not for follow-up questions
        if (isNewSearch) {
          const propertiesMessage: Message = {
            id: (Date.now() + 2).toString(),
            type: 'properties',
            content: `Found ${formattedProperties.length} properties matching your criteria!`,
            properties: formattedProperties,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, propertiesMessage]);
        }
        
        // Debug logging for aisearchportal
        console.log('🏠 [PropertyDisplay] State after receiving properties:', {
          propertiesCount: formattedProperties.length,
          showProperties,
          leadId,
          hasShownSaveSearchModal,
          isAISearchPortal: window.location.hostname.includes('aisearchportal.com') || window.location.hostname.includes('kairosiq.ai')
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: "I apologize, but I'm having trouble connecting to our property search service. Please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSelectPrompt = (prompt: string) => {
    if (inputRef.current) inputRef.current.value = prompt;
    inputRef.current?.focus();
    handleSendMessage(prompt);
  };

  // Listing card component for inline results
  const ListingCard: React.FC<{ listing: any }> = ({ listing }) => {
    return (
      <Card className="mb-3 shadow-sm">
        <Row className="g-0">
          <Col md={4}>
            <img 
              src={listing.image} 
              className="img-fluid rounded-start h-100" 
              alt={listing.address}
              style={{ objectFit: 'cover' }}
            />
          </Col>
          <Col md={8}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h5 className="card-title">{listing.address}</h5>
                  <p className="h4 text-primary mb-2">{listing.price}</p>
                  <p className="text-muted mb-2">
                    {listing.beds} beds • {listing.baths} baths • {listing.sqft} sqft
                  </p>
                </div>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate(`/property/${listing.mlsNumber}`)}
                >
                  View Details
                </Button>
              </div>
              <div className="alert alert-light mt-3 mb-0">
                <small>
                  <strong>Why this matches:</strong> {listing.whyItMatches}
                </small>
              </div>
            </Card.Body>
          </Col>
        </Row>
      </Card>
    );
  };

  // Message component
  const ChatMessage: React.FC<{ message: Message }> = ({ message }) => {
    if (message.type === 'user') {
      return (
        <div className="d-flex justify-content-end mb-3">
          <div className="bg-primary text-white rounded-3 px-3 py-2" style={{ maxWidth: '70%' }}>
            {message.content}
          </div>
        </div>
      );
    }

    if (message.type === 'assistant') {
      return (
        <div className="d-flex flex-column align-items-start mb-3">
          <div className="bg-light rounded-3 px-3 py-2 mb-2" style={{ maxWidth: '70%' }}>
            {message.content}
          </div>
          {/* KOPS-343: Suggested actions for guided conversation */}
          {message.suggestedActions && message.suggestedActions.length > 0 && (
            <div className="d-flex flex-wrap gap-2 ms-2" style={{ maxWidth: '70%' }}>
              {message.suggestedActions.map((action, idx) => (
                <Button
                  key={idx}
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleSelectPrompt(action.label)}
                  className="rounded-pill"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (message.type === 'properties' && message.properties) {
      return (
        <div className="properties-results my-3">
          <InlinePropertyCards 
            properties={message.properties}
            maxDisplay={3}
            onViewAll={() => {
              // This will be handled by the parent component
              const event = new CustomEvent('viewAllProperties', { 
                detail: { properties: message.properties } 
              });
              window.dispatchEvent(event);
            }}
            onPropertyClick={(property) => {
              setSelectedProperty(property);
              setShowPropertyModal(true);
              trackBuyerActivity('property_view', {
                address: `${property.address}, ${property.city || ''}`,
                price: property.price,
                mlsNumber: property.mlsNumber || (property as any).mls_number,
              }, (agentProfile as any)?.userId);
            }}
          />
        </div>
      );
    }

    if (message.type === 'listings' && message.listings) {
      return (
        <div className="listings-results my-4">
          <h5 className="mb-3">Here are homes that match what you're looking for:</h5>
          {message.listings.map((listing, idx) => (
            <ListingCard key={idx} listing={listing} />
          ))}
          <div className="text-center mt-3">
            <Button 
              variant="outline-primary" 
              className="me-2"
              onClick={() => {
                const context = chatService.current.getContext();
                navigate('/search', { state: { criteria: context.extractedInfo || context.extractedCriteria } });
              }}
            >
              Show more results
            </Button>
            <Button 
              variant="primary"
              onClick={() => {
                const context = chatService.current.getContext();
                navigate('/search?view=map', { state: { criteria: context.extractedInfo } });
              }}
            >
              View on map
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="chat-interface-v3" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <MinimalHeader agent={agentProfile} isAISearchPortal={isAISearchPortal} onContactAgent={() => setShowContactPanel(true)} />
      
      <Container fluid className="flex-grow-1 d-flex" style={{ height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
        <Row className="w-100 g-0" style={{ height: '100%' }}>
          {/* Main chat area */}
          <Col 
            lg={8} 
            className={`d-flex flex-column ${isMobile && showProperties && mobileTab !== 'chat' ? 'd-none' : ''}`} 
            style={{ height: '100%' }}>
            {/* Messages area */}
            <div className="flex-grow-1 overflow-auto p-4" style={{ background: '#f8f9fa' }}>
              <Container style={{ maxWidth: '800px' }}>
                {messages.length === 0 && (
                  <>
                    <div className="text-center py-5">
                      <h2 className="mb-3">Welcome to {window.location.hostname.includes('aisearchportal.com') || window.location.hostname.includes('kairosiq.ai') ? 'AI Search Portal' : 'chat.realestate'}</h2>
                      <p className="lead text-muted">
                        {window.location.hostname.includes('aisearchportal.com') || window.location.hostname.includes('kairosiq.ai') 
                          ? `${agentProfile.name} has curated property searches for you. Let me help you explore these options.`
                          : 'Tell me about your ideal home and I\'ll help you find properties that match your needs.'
                        }
                      </p>
                    </div>
                    
                    {/* Agent Search History Panel - Show when buyer context has searches */}
                    {agentSearchHistory.length > 0 && (
                      <div className="agent-searches-panel mb-4 p-4 bg-light rounded">
                        <h5 className="mb-3">
                          <FontAwesomeIcon icon={faList} className="me-2 text-primary" />
                          Your agent {agentProfile.name} has searched for:
                        </h5>
                        <Row className="g-2">
                          {agentSearchHistory.slice(0, 6).map((search: any) => (
                            <Col key={search.id} xs={12} md={6}>
                              <Button 
                                variant="outline-primary"
                                className="w-100 text-start p-3"
                                onClick={() => {
                                  console.log('🔍 [HomePageV3] Clicking agent search:', search);
                                  (window as any).loadAgentSearch(search.id, search.query);
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong className="d-block">"{search.query}"</strong>
                                    <small className="text-muted">
                                      {search.resultsCount} properties • {new Date(search.createdAt).toLocaleDateString()}
                                    </small>
                                  </div>
                                  <Badge bg="secondary">{search.resultsCount}</Badge>
                                </div>
                              </Button>
                            </Col>
                          ))}
                        </Row>
                        {agentSearchHistory.length > 6 && (
                          <div className="text-center mt-3">
                            <small className="text-muted">Showing 6 of {agentSearchHistory.length} searches</small>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <SuggestedPrompts onSelectPrompt={handleSelectPrompt} />
                  </>
                )}

                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}

                {isTyping && (
                  <div className="d-flex justify-content-start mb-3">
                    <div className="bg-light rounded-3 px-3 py-2">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Context indicator */}
                {messages.length > 0 && !hasEnoughContext && (
                  <div className="text-center text-muted small mb-3">
                    <em>Tell me more about your location and preferences to see matching properties...</em>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </Container>
            </div>

            {/* Input area */}
            <div className="border-top p-3" style={{ background: 'white' }}>
              <Container style={{ maxWidth: '800px' }}>
                <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}>
                  <div className="input-group">
                    <input
                      ref={inputRef}
                      type="text"
                      className="form-control form-control-lg"
                      placeholder={isListening ? "Listening... speak now" : "Describe what you're looking for in a home..."}
                      defaultValue={inputValue}
                      style={{ borderRight: 'none' }}
                    />
                    <Button 
                      type="button"
                      variant={isListening ? "danger" : "outline-secondary"}
                      onClick={toggleVoiceInput}
                      style={{ borderLeft: 'none', borderRight: 'none' }}
                      title={isListening ? "Stop recording" : "Use voice search"}
                    >
                      <FontAwesomeIcon icon={isListening ? faStop : faMicrophone} />
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </Button>
                  </div>
                  <small className="text-muted mt-1 d-block">
                    No sign-up required. Your conversation helps us understand your needs.
                  </small>
                </form>
              </Container>
            </div>
          </Col>

          {/* Side panel - transforms from info to properties */}
          <Col 
            lg={4} 
            className={`border-start ${isMobile ? (showProperties && mobileTab !== 'chat' ? 'mobile-property-panel active' : 'd-none') : 'd-none d-lg-block'}`} 
            style={{ 
              background: showProperties ? 'white' : '#f8f9fa', 
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease' 
            }}>
            {showContactPanel ? (
              <ContactAgentPanel
                agent={agentProfile}
                leadId={leadId}
                agentId={(agentProfile as any).userId || localStorage.getItem('portalAgentId') || ''}
                context="buyer"
                onClose={() => setShowContactPanel(false)}
              />
            ) : !showProperties ? (
              // Use the new RightPanelWithLeadCapture component
              messages.length === 0 ? (
                // Information panel replaced by RightPanelWithLeadCapture
                <RightPanelWithLeadCapture
                  sessionId={chatService.current?.getSessionId() || ''}
                  onEmailCapture={handleEmailCapture}
                  hasMessages={false}
                />
              ) : (
                // When messages exist, show RightPanelWithLeadCapture with active messaging
                <RightPanelWithLeadCapture
                  sessionId={chatService.current?.getSessionId() || ''}
                  onEmailCapture={handleEmailCapture}
                  hasMessages={true}
                />
              )) : (
              // Properties panel with list/map toggle
              (() => {
                console.log('🎯 [PropertyResultsPanel] About to render with:', {
                  showProperties,
                  propertiesCount: properties.length,
                  properties: properties.slice(0, 2) // Show first 2 for debugging
                });
                return (
                  <PropertyResultsPanel
                    properties={properties}
                    viewMode={panelViewMode}
                    onViewModeChange={setPanelViewMode}
                    onPropertySelect={(property) => {
                  setSelectedProperty(property);
                  setShowPropertyModal(true);
                  trackBuyerActivity('property_view', {
                    address: `${property.address}, ${property.city || ''}`,
                    price: property.price,
                    mlsNumber: property.mlsNumber || (property as any).mls_number,
                  }, (agentProfile as any)?.userId);
                }}
                searchCriteria={searchCriteria}
                onUpdateFilters={(filters) => {
                  // Update search criteria and trigger new search
                  setSearchCriteria({ ...searchCriteria, ...filters });
                  const newMessage = `Show me properties ${filters.maxPrice ? `under $${filters.maxPrice.toLocaleString()}` : ''} ${filters.bedrooms ? `with ${filters.bedrooms}+ bedrooms` : ''}`;
                  handleSendMessage(newMessage.trim());
                }}
                savedPropertyIds={savedPropertyIds}
                onToggleSave={handleToggleSaveProperty}
                showMapButton={showMapButton}
                onViewMap={onViewMap}
                  />
                );
              })()
            )}
          </Col>
        </Row>
      </Container>
      
      {/* Mobile Tab Bar */}
      {isMobile && showProperties && (
        <div className="mobile-tab-bar">
          <button
            className={`mobile-tab ${mobileTab === 'chat' ? 'active' : ''}`}
            onClick={() => setMobileTab('chat')}
          >
            <FontAwesomeIcon icon={faComments} className="mobile-tab-icon" />
            <span className="mobile-tab-label">Chat</span>
          </button>
          
          <button
            className={`mobile-tab ${mobileTab === 'list' ? 'active' : ''}`}
            onClick={() => {
              setMobileTab('list');
              setPanelViewMode('list');
            }}
          >
            <FontAwesomeIcon icon={faList} className="mobile-tab-icon" />
            <span className="mobile-tab-label">List</span>
            <span className="mobile-tab-badge">{properties.length}</span>
          </button>
          
          <button
            className={`mobile-tab ${mobileTab === 'map' ? 'active' : ''}`}
            onClick={() => {
              setMobileTab('map');
              setPanelViewMode('map');
            }}
          >
            <FontAwesomeIcon icon={faMap} className="mobile-tab-icon" />
            <span className="mobile-tab-label">Map</span>
          </button>
        </div>
      )}
      
      {/* Save Search Modal */}
      <SaveSearchModal
        open={showSaveSearchModal}
        onClose={() => setShowSaveSearchModal(false)}
        onSave={async (data) => {
          console.log('💾 [SAVE SEARCH] User data:', data);
          
          try {
            // Create buyer profile using the public endpoint
            const sessionId = chatService.current?.getSessionId() || `chat_${Date.now()}`;
            
            const response = await axiosInstance.post('/public-buyer-profile/create-profile', {
              sessionId,
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              phone: data.phone,
              propertyPreferences: {
                bedrooms: searchCriteria.beds,
                bathrooms: searchCriteria.baths,
                property_type: searchCriteria.propertyType || 'Single Family',
                budget_min: searchCriteria.minPrice,
                budget_max: searchCriteria.maxPrice,
                features: searchCriteria.features
              },
              locationPreferences: {
                preferred_cities: searchCriteria.city ? [searchCriteria.city] : [],
                preferred_state: searchCriteria.state || 'Arizona'
              },
              buyingTimeframe: searchCriteria.timeline || 'Just Looking'
            });

            if (response.data.success) {
              // Save the lead ID for future property saves
              if (response.data.leadId) {
                setLeadId(response.data.leadId);
              }
              
              // Subscribe to alerts based on user preference
              if (data.agreeToTerms && data.alertFrequency) {
                await axiosInstance.post('/public-buyer-profile/subscribe-alerts', {
                  sessionId,
                  email: data.email,
                  searchCriteria: searchCriteria,
                  frequency: data.alertFrequency
                });
              }
              
              // Store lead ID for future actions
              localStorage.setItem('chat_lead_id', response.data.leadId);
              
              // Show success message
              const successMessage: Message = {
                id: Date.now().toString(),
                type: 'system',
                content: `✅ Great! I've saved your search and will email you at ${data.email} when new properties match. Here are your current matches:`,
                timestamp: new Date()
              };
              setMessages(prev => [...prev, successMessage]);
            }
          } catch (error) {
            console.error('Error saving search:', error);
            // Show error message
            const errorMessage: Message = {
              id: Date.now().toString(),
              type: 'system',
              content: `⚠️ There was an issue saving your search. You can still view the properties below:`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
          }
          
          // Show the properties regardless
          setShowSaveSearchModal(false);
          setShowProperties(true);
        }}
        searchCriteria={searchCriteria}
        matchCount={properties.length || searchProgress.matchCount}
      />
      
      {/* Property Details Modal */}
      <PropertyDetailsModal
        show={showPropertyModal}
        property={selectedProperty}
        onHide={() => setShowPropertyModal(false)}
        onSave={(property) => {
          console.log('Property saved:', property);
          // Handle save functionality if needed
        }}
        isSaved={false}
      />
      
      {/* Appointment Scheduling Modal */}
      {leadId && (
        <AppointmentModal
          show={showAppointmentModal}
          onHide={() => setShowAppointmentModal(false)}
          properties={appointmentProperties}
          leadId={leadId}
          agentId={agentProfile.userId || ''} 
          agentName={agentProfile.name}
          onSuccess={() => {
            // Show success message in chat
            const successMessage: Message = {
              id: Date.now().toString(),
              type: 'system',
              content: `✅ Great! I've submitted your appointment request to ${agentProfile.name}. They'll contact you shortly to confirm the viewing times.`,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, successMessage]);
          }}
        />
      )}

      {/* Fair Housing Footer - Arizona Compliance */}
      <footer className="border-top mt-auto py-3" style={{ background: '#f8f9fa' }}>
        <Container fluid>
          <Row className="align-items-center justify-content-center text-center">
            <Col xs="auto">
              <img
                src={require('../../assets/img/logos/equal-housing-opportunity-logo-1200w.jpg')}
                alt="Equal Housing Opportunity"
                style={{ height: '30px', objectFit: 'contain' }}
              />
            </Col>
            <Col xs="auto">
              <small className="text-muted">
                Equal Housing Opportunity.
              </small>
            </Col>
          </Row>
          {/* ARMLS IDX 23.3.3 + 23.3.4 — source attribution and VERBATIM
              disclaimer (July 2026 ActiveAgent audit cure — do not reword) */}
          <Row className="justify-content-center text-center mt-1">
            <Col xs={12} md={10}>
              <small className="text-muted" style={{ fontSize: '11px' }}>
                Listing data provided by Arizona Regional Multiple Listing Service (ARMLS).
                All information should be verified by the recipient and none is guaranteed as accurate by ARMLS.
                &copy; {new Date().getFullYear()} Arizona Regional Multiple Listing Service, Inc.
              </small>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

// Wrap in React.memo to prevent unnecessary re-renders
export default React.memo(HomePageV3);